import { useQuery } from "react-query";
import { z } from "zod";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import {
    AdminConsentRecordDecisionSchema,
    AdminConsentRecordSourceSchema,
    AdminConsentRecordTypeSchema,
} from "./adminConsentRecord";
import { buildConsentLocaleHeaders, normalizeConsentLocale } from "./locale";

const UserReferenceSchema = z
    .object({
        "@id": z.string().optional(),
        id: z.number().optional(),
        firstname: z.string().optional(),
        lastname: z.string().optional(),
        email: z.string().optional(),
    })
    .passthrough();

const ConsentItemReferenceSchema = z.union([
    z.string(),
    z
        .object({
            "@id": z.string().optional(),
            id: z.number(),
            code: z.string().optional(),
            name: z.string().optional(),
            type: AdminConsentRecordTypeSchema.optional(),
        })
        .passthrough(),
]);

const ConsentItemVersionReferenceSchema = z.union([
    z.string(),
    z
        .object({
            "@id": z.string().optional(),
            id: z.number(),
            version: z.string().optional(),
            title: z.string().optional(),
        })
        .passthrough(),
]);

const ConsentGovernanceItemSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    code: z.string(),
    type: z.enum(["document", "opt_in"]),
    name: z.string(),
    description: z.string().nullable().optional(),
    required: z.boolean(),
    active: z.boolean(),
    sortOrder: z.number(),
    category: z.string().nullable().optional(),
    reconsentImpactEstimate: z.number().optional().default(0),
    createdAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
});

const ConsentGovernanceVersionSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    consentItem: ConsentItemReferenceSchema,
    version: z.string(),
    title: z.string(),
    summary: z.string().nullable().optional(),
    content: z.string().nullable().optional(),
    documentUrl: z.string().nullable().optional(),
    published: z.boolean(),
    publishedAt: z.string().nullable().optional(),
    archivedAt: z.string().nullable().optional(),
    status: z.enum(["draft", "published", "archived"]).optional(),
    createdAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
});

const ConsentGovernanceRecordSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    user: z.union([z.string(), UserReferenceSchema]),
    consentItem: ConsentItemReferenceSchema,
    consentItemVersion: ConsentItemVersionReferenceSchema,
    decision: AdminConsentRecordDecisionSchema,
    source: AdminConsentRecordSourceSchema,
    decidedAt: z.string(),
    createdAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
});

export type ConsentGovernanceItem = z.infer<typeof ConsentGovernanceItemSchema>;
export type ConsentGovernanceVersion = z.infer<typeof ConsentGovernanceVersionSchema>;
export type ConsentGovernanceRecord = z.infer<typeof ConsentGovernanceRecordSchema>;

type CollectionOptions = {
    page?: number;
    itemsPerPage?: number;
    enabled?: boolean;
    fetchAllPages?: boolean;
    locale?: string;
    queryParams?: Record<string, boolean | number | string | undefined>;
};

const useConsentGovernanceCollection = <TSchema extends z.ZodTypeAny>(
    queryKeyBase: string,
    endpoint: string,
    schema: TSchema,
    options?: CollectionOptions,
) => {
    const { page = 1, itemsPerPage = 50, enabled = true, fetchAllPages = false, locale, queryParams } = options ?? {};
    const normalizedParams = Object.entries(queryParams ?? {})
        .filter(([, value]) => value !== undefined)
        .sort(([left], [right]) => left.localeCompare(right));
    const normalizedLocale = normalizeConsentLocale(locale) ?? null;
    const requestPage = fetchAllPages ? 1 : page;

    return useQuery({
        queryKey: [
            queryKeyBase,
            normalizedLocale,
            requestPage,
            itemsPerPage,
            fetchAllPages,
            ...normalizedParams.flat(),
        ],
        enabled,
        staleTime: 0,
        queryFn: async () => {
            const getPage = (pageToFetch: number) =>
                loggedInApiCall.get(endpoint, {
                    headers: buildConsentLocaleHeaders(locale),
                    params: {
                        page: pageToFetch,
                        itemsPerPage,
                        ...Object.fromEntries(normalizedParams),
                    },
                });

            const response = await getPage(requestPage);

            if (!fetchAllPages) {
                return response;
            }

            const firstPageItems = Array.isArray(response.data["hydra:member"]) ? response.data["hydra:member"] : [];
            const totalItems =
                typeof response.data["hydra:totalItems"] === "number" ?
                    response.data["hydra:totalItems"]
                :   firstPageItems.length;
            const pageCount = Math.ceil(totalItems / itemsPerPage);

            if (pageCount <= requestPage) {
                return response;
            }

            const remainingPages = Array.from(
                { length: pageCount - requestPage },
                (_, index) => requestPage + index + 1,
            );
            const remainingResponses = await Promise.all(remainingPages.map((pageToFetch) => getPage(pageToFetch)));
            const remainingItems = remainingResponses.flatMap((pageResponse) =>
                Array.isArray(pageResponse.data["hydra:member"]) ? pageResponse.data["hydra:member"] : [],
            );

            return {
                ...response,
                data: {
                    ...response.data,
                    "hydra:member": [...firstPageItems, ...remainingItems],
                    "hydra:totalItems": totalItems,
                },
            };
        },
        select: (response) => {
            const items = zodParseWithDetailedLog(response.data["hydra:member"] ?? [], schema.array(), {
                operation: "GET",
                endpoint,
            });

            return {
                items,
                totalItems: response.data["hydra:totalItems"] ?? items.length,
            };
        },
    });
};

export const getAdminConsentItemsKey = "getAdminConsentItems" as const;
export const getAdminConsentItemVersionsKey = "getAdminConsentItemVersions" as const;
export const getAdminConsentRecordsKey = "getAdminConsentRecords" as const;

export const useGetAdminConsentItems = (options?: CollectionOptions) => {
    const query = useConsentGovernanceCollection(
        getAdminConsentItemsKey,
        "/api/consent-items",
        ConsentGovernanceItemSchema,
        options,
    );

    return {
        consentItems: query.data?.items,
        totalItems: query.data?.totalItems,
        ...query,
    };
};

export const useGetAdminConsentItemVersions = (options?: CollectionOptions) => {
    const query = useConsentGovernanceCollection(
        getAdminConsentItemVersionsKey,
        "/api/consent-item-versions",
        ConsentGovernanceVersionSchema,
        options,
    );

    return {
        consentItemVersions: query.data?.items,
        totalItems: query.data?.totalItems,
        ...query,
    };
};

export const useGetAdminConsentRecords = (options?: CollectionOptions) => {
    const query = useConsentGovernanceCollection(
        getAdminConsentRecordsKey,
        "/api/consent-records",
        ConsentGovernanceRecordSchema,
        options,
    );

    return {
        consentRecords: query.data?.items,
        totalItems: query.data?.totalItems,
        ...query,
    };
};
